/**
 * Keyboard Shortcuts Manager for Dance Music Player
 * Handles key bindings for playback, seeking, speed, and modal navigation.
 */

export interface ShortcutActions {
    onPlayPause: () => void;
    onNext: () => void;
    onPrev: () => void;
    onSeek: (seconds: number) => void;
    onSeekPercent: (num: number) => void;
    onVolumeChange: (delta: number) => void;
    onToggleMute: () => void;
    onSpeedChange: (delta: number) => void;
    onSpeedReset: () => void;
    onToggleShuffle: () => void;
    onToggleRepeat: () => void;
    onToggleVisualizer: () => void;
    onOpenShortcuts: () => void;
    onCloseModals: () => void;
}

export class KeyboardShortcutsManager {
    private actions: ShortcutActions;
    private searchInput: HTMLInputElement;

    constructor(actions: ShortcutActions, searchInput: HTMLInputElement) {
        this.actions = actions;
        this.searchInput = searchInput;
        this.bindEvents();
    }

    private bindEvents(): void {
        document.addEventListener('keydown', (e: KeyboardEvent) => {
            const activeElement = document.activeElement;
            const isSearchFocused = activeElement === this.searchInput;
            const isEditableFocused = activeElement instanceof HTMLElement && (
                activeElement.matches('input, textarea, select') ||
                activeElement.isContentEditable
            );

            // Escape key always works
            if (e.key === 'Escape') {
                if (isEditableFocused) {
                    activeElement.blur();
                }
                this.actions.onCloseModals();
                return;
            }

            // Search focus shortcut
            if ((e.key === '/' || (e.ctrlKey && e.key.toLowerCase() === 'f')) && !isEditableFocused) {
                e.preventDefault();
                this.searchInput.focus();
                this.searchInput.select();
                return;
            }

            if (isEditableFocused) return;

            const key = e.key;

            switch (key) {
                // Play / Pause
                case ' ':
                case 'k':
                case 'K':
                    e.preventDefault();
                    this.actions.onPlayPause();
                    break;

                // Seeking Left / Right
                case 'ArrowLeft':
                    e.preventDefault();
                    this.actions.onSeek(e.shiftKey ? -10 : -5);
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    this.actions.onSeek(e.shiftKey ? 10 : 5);
                    break;
                case 'j':
                case 'J':
                    e.preventDefault();
                    this.actions.onSeek(-10);
                    break;
                case 'l':
                case 'L':
                    e.preventDefault();
                    this.actions.onSeek(10);
                    break;

                // Volume Up / Down / Mute
                case 'ArrowUp':
                    e.preventDefault();
                    this.actions.onVolumeChange(0.05);
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    this.actions.onVolumeChange(-0.05);
                    break;
                case 'm':
                case 'M':
                    e.preventDefault();
                    this.actions.onToggleMute();
                    break;

                // Speed Controls
                case '[':
                case '<':
                case ',':
                    e.preventDefault();
                    this.actions.onSpeedChange(-0.05);
                    break;
                case ']':
                case '>':
                case '.':
                    e.preventDefault();
                    this.actions.onSpeedChange(0.05);
                    break;
                case '0':
                    e.preventDefault();
                    this.actions.onSpeedReset();
                    break;
                // Navigation
                case 'n':
                case 'N':
                    e.preventDefault();
                    this.actions.onNext();
                    break;
                case 'p':
                case 'P':
                    e.preventDefault();
                    this.actions.onPrev();
                    break;

                // Shuffle & Repeat
                case 's':
                case 'S':
                    e.preventDefault();
                    this.actions.onToggleShuffle();
                    break;
                case 'e':
                case 'E':
                    e.preventDefault();
                    this.actions.onToggleRepeat();
                    break;

                // Visualizer & Help
                case 'f':
                case 'F':
                    e.preventDefault();
                    this.actions.onToggleVisualizer();
                    break;
                case '?':
                case 'h':
                case 'H':
                    e.preventDefault();
                    this.actions.onOpenShortcuts();
                    break;

                // Jump to % of Track (1-9)
                case '1': case '2': case '3': case '4': case '5':
                case '6': case '7': case '8': case '9':
                    if (!e.ctrlKey && !e.altKey && !e.metaKey) {
                        e.preventDefault();
                        this.actions.onSeekPercent(parseInt(key, 10));
                    }
                    break;
            }
        });
    }
}
